import React, { useState, useEffect } from "react";
import { MdArrowBack } from "react-icons/md";
import toast from "react-hot-toast";
import { createCustomer, UpdatecustomerbyId } from "../../../api/api";

// ─── Section Header ─────────────────────────────────────────
const SectionHeader = ({ number, title, subtitle }) => (
  <div className="flex items-center gap-4 mb-6">
    <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center">
      <span className="text-white text-sm font-bold">{number}</span>
    </div>
    <div>
      <h2 className="text-sm font-bold uppercase tracking-widest text-gray-900">
        {title}
      </h2>
      {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
    </div>
    <div className="flex-1 h-px bg-gray-100 ml-2" />
  </div>
);

// ─── Field Wrapper ──────────────────────────────────────────
const Field = ({ label, required, children }) => (
  <div className="flex flex-col gap-1.5">
    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
      {label}
      {required && <span className="ml-1">*</span>}
    </label>
    {children}
  </div>
);

// ─── Input Style ────────────────────────────────────────────
const inputCls = (disabled) =>
  `w-full px-3 py-2.5 text-sm rounded-lg border ${
    disabled
      ? "bg-gray-50 text-gray-400 border-gray-200"
      : "bg-white border-gray-200 focus:border-gray-500 focus:ring-2 focus:ring-gray-50"
  }`;

// ─── Main Component ─────────────────────────────────────────
const AddCustomer = ({ onBack, isView, isEdit, customer }) => {
  const [data, setData] = useState({
    companyName: "",
    name: "",
    contactNo: "",
    email: "",
    gstNo: "",
    address: "",
    companyPhoto: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (customer && (isEdit || isView)) {
      setData(customer);
    }
  }, [customer, isEdit, isView]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((p) => ({ ...p, [name]: value }));
  };

  const handleFile = (e) => {
    setData((p) => ({ ...p, companyPhoto: e.target.files[0] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isEdit) {
        await UpdatecustomerbyId(customer._id, data);
      } else {
        await createCustomer(data);
      }
      toast.success("Saved successfully");
      onBack();
    } catch (err) {
      toast.error(err.response?.data?.message || "Error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top Bar */}
      <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <MdArrowBack
            className="text-xl cursor-pointer"
            onClick={onBack}
          />
          <div>
            <h1 className="font-bold text-gray-900">
              {isView ? "View Customer" : isEdit ? "Edit Customer" : "New Customer"}
            </h1>
            <p className="text-xs text-gray-400">
              {isView
                ? "Read only"
                : isEdit
                ? "Update customer details"
                : "Create a new customer"}
            </p>
          </div>
        </div>

        {!isView && (
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-5 py-2 text-sm font-bold text-white rounded-lg ${
              isSubmitting ? "bg-gray-400" : "bg-black hover:bg-gray-700"
            }`}
          >
            {isSubmitting ? "Saving..." : isEdit ? "Update" : "Save"}
          </button>
        )}
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-8">
        {/* Section 1 */}
        <div className="bg-white p-6 rounded-2xl border shadow-sm">
          <SectionHeader
            number="01"
            title="Company Details"
            subtitle="Basic company information"
          />

          <div className="grid md:grid-cols-3 gap-5">
            <Field label="Company Name" required>
              <input
                name="companyName"
                value={data.companyName}
                onChange={handleChange}
                disabled={isView}
                className={inputCls(isView)}
              />
            </Field>

            <Field label="Contact Person" required>
              <input
                name="name"
                value={data.name}
                onChange={handleChange}
                disabled={isView}
                className={inputCls(isView)}
              />
            </Field>

            <Field label="Contact Number" required>
              <input
                name="contactNo"
                value={data.contactNo}
                onChange={handleChange}
                disabled={isView}
                className={inputCls(isView)}
              />
            </Field>

            <Field label="Email" required>
              <input
                name="email"
                value={data.email}
                onChange={handleChange}
                disabled={isView || isEdit}
                className={inputCls(isView)}
              />
            </Field>

            <Field label="GST Number" required>
              <input
                name="gstNo"
                value={data.gstNo}
                onChange={handleChange}
                disabled={isView}
                className={inputCls(isView)}
              />
            </Field>

            {!isView && (
              <Field label="Company Photo">
                <input type="file" onChange={handleFile} />
              </Field>
            )}
          </div>

          {customer?.companyPhoto && (
            <div className="mt-4">
              <p className="text-xs text-gray-400 mb-1">Existing Photo</p>
              <img
                src={customer.companyPhoto}
                alt="company"
                className="w-24 h-24 rounded-lg border"
              />
            </div>
          )}
        </div>

        {/* Section 2 */}
        <div className="bg-white p-6 rounded-2xl border shadow-sm">
          <SectionHeader
            number="02"
            title="Address"
            subtitle="Customer location details"
          />

          <Field label="Address" required>
            <textarea
              name="address"
              value={data.address}
              onChange={handleChange}
              disabled={isView}
              rows={3}
              className={inputCls(isView)}
            />
          </Field>
        </div>

        {isView && (
          <div className="flex justify-end">
            <button
              onClick={onBack}
              className="px-5 py-2 border rounded-lg text-gray-600"
            >
              ← Back
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddCustomer;